import { useCallback, useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import type { PlotMouseEvent } from "plotly.js";
import type { ModelCoverageWindow } from "src/store/api/generatedApi";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";
import {
  buildStackedBarTraces,
  formatHoverTimeRange,
  stackedChartLayout,
  timelineChartConfig,
  TimelineLegend,
} from "../components/timelineChartUtils";

type ModelCoverageTimelineProps = Readonly<{
  windows: readonly ModelCoverageWindow[];
  start: Date;
  end: Date;
  onWindowClick?: (window: ModelCoverageWindow) => void;
}>;

const LABELS_COLOR = "#4caf50";
const PREDICTIONS_COLOR = "#ffc107";
const EMBEDDED_COLOR = "#1565c0";
const TRANSFORMED_COLOR = "#2196f3";
const VIDEO_COLOR = "#81d4fa";
const NO_COVERAGE_COLOR = "#e0e0e0";

export interface Bands {
  labels: number;
  predictions: number;
  embedded: number;
  transformed: number;
  video: number;
  noCoverage: number;
}

const effectiveCoverage = (window: ModelCoverageWindow) => {
  const effVideo = window.video_coverage_ratio;
  const effTransform = Math.min(effVideo, window.transform_coverage_ratio);
  const effEmbedding = Math.min(effTransform, window.embedding_coverage_ratio);
  const effPrediction = Math.min(
    effEmbedding,
    window.prediction_coverage_ratio
  );
  const effLabels = Math.min(effPrediction, window.label_coverage_ratio);
  return { effVideo, effTransform, effEmbedding, effPrediction, effLabels };
};

export const computeBands = (window: ModelCoverageWindow): Bands => {
  const { effVideo, effTransform, effEmbedding, effPrediction, effLabels } =
    effectiveCoverage(window);

  return {
    labels: effLabels,
    predictions: effPrediction - effLabels,
    embedded: effEmbedding - effPrediction,
    transformed: effTransform - effEmbedding,
    video: effVideo - effTransform,
    noCoverage: 1.0 - effVideo,
  };
};

const STAGE_CONFIG = [
  { key: "labels" as const, color: LABELS_COLOR, name: "Labels" },
  {
    key: "predictions" as const,
    color: PREDICTIONS_COLOR,
    name: "Predictions",
  },
  { key: "embedded" as const, color: EMBEDDED_COLOR, name: "Embedded" },
  {
    key: "transformed" as const,
    color: TRANSFORMED_COLOR,
    name: "Transformed",
  },
  { key: "video" as const, color: VIDEO_COLOR, name: "Video" },
  { key: "noCoverage" as const, color: NO_COVERAGE_COLOR, name: "No data" },
] as const;

const windowHoverText = (
  window: ModelCoverageWindow,
  clickHint: string,
  timeZone: string
): string => {
  const eff = effectiveCoverage(window);
  const lines = [
    `<b>${formatHoverTimeRange(window.window_start, window.window_end, timeZone)}</b>`,
    `Video: ${(eff.effVideo * 100).toFixed(1)}%`,
    `Transformed: ${(eff.effTransform * 100).toFixed(1)}%`,
    `Embedded: ${(eff.effEmbedding * 100).toFixed(1)}%`,
    `Predictions: ${(eff.effPrediction * 100).toFixed(1)}%`,
    `Labels: ${(eff.effLabels * 100).toFixed(1)}%`,
    `<span style="color:#4441C8"><i>${clickHint}</i></span>`,
  ];
  return lines.join("<br>");
};

export const ModelCoverageTimeline = ({
  windows,
  start,
  end,
  onWindowClick,
}: ModelCoverageTimelineProps) => {
  const { t } = useTenantTranslation();
  const timeZone = useResolvedScrapViewportTimeZone();
  const theme = useTheme();

  const clickHint = t("modelCoverageClickToView");
  const lineColor = theme.palette.background.default;
  const traces = useMemo(
    () =>
      buildStackedBarTraces({
        windows,
        stageConfig: STAGE_CONFIG,
        computeBands,
        hoverTextFn: (window) => windowHoverText(window, clickHint, timeZone),
        lineColor,
      }),
    [windows, clickHint, timeZone, lineColor]
  );
  const layout = useMemo(
    () => stackedChartLayout(start, end, theme),
    [start, end, theme]
  );

  const handleClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      const pointNumber = event.points[0]?.pointNumber;
      if (pointNumber === undefined || !onWindowClick) return;
      const clicked = windows[pointNumber];
      if (clicked) onWindowClick(clicked);
    },
    [windows, onWindowClick]
  );

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle2">{t("modelCoverageTitle")}</Typography>
        <TimelineLegend
          color={LABELS_COLOR}
          label={t("modelCoverageLabelsProduced")}
        />
        <TimelineLegend
          color={PREDICTIONS_COLOR}
          label={t("modelCoverageInferenceRun")}
        />
        <TimelineLegend
          color={EMBEDDED_COLOR}
          label={t("modelCoverageEmbedded")}
        />
        <TimelineLegend
          color={TRANSFORMED_COLOR}
          label={t("modelCoverageTransformed")}
        />
        <TimelineLegend
          color={VIDEO_COLOR}
          label={t("modelCoverageHasVideo")}
        />
        <TimelineLegend
          color={NO_COVERAGE_COLOR}
          label={t("modelCoverageNoData")}
        />
      </Stack>
      <Box sx={{ width: "100%" }}>
        <ScrapTrackingViewportPlot
          data={traces}
          layout={layout}
          config={timelineChartConfig}
          style={{ width: "100%", cursor: "pointer" }}
          onClick={handleClick}
        />
      </Box>
    </Stack>
  );
};
